import { useMemo } from "react";
import { OverlayView, Polyline } from "@react-google-maps/api";

import { useCentinelaUnitConduccion } from "../../../features/centinela/queries";

/**
 * El día de una unidad dibujado sobre el mapa: por dónde anduvo y qué le pasó.
 *
 * ── Qué reemplaza ───────────────────────────────────────────────────────────
 * El historial de casos vivía en una columna fija a la derecha, siempre
 * abierta, como una lista de horas y etiquetas. Una lista contesta «qué pasó»
 * pero nunca «dónde», y en una flota lo segundo suele explicar lo primero:
 * cinco cortes de señal repartidos por la ciudad son un problema del equipo;
 * los mismos cinco en el mismo tramo de sierra son cobertura.
 *
 * ── De dónde sale ───────────────────────────────────────────────────────────
 * De un solo endpoint, el mismo que alimenta la pestaña de Conducción. El
 * recorrido se pide aparte (`recorrido: true`) porque son miles de puntos y
 * sólo el mapa los necesita.
 *
 * ── Por qué hay hechos que NO se dibujan ────────────────────────────────────
 * Los que llegan con `ubicado: false`: la cámara no da coordenadas y no hubo un
 * punto del recorrido lo bastante cerca en el tiempo. Se cuentan aparte y se
 * dicen en el pie. Poner un pin donde no se sabe que estuvo es peor que no
 * ponerlo — en un mapa un pin es una afirmación, y nadie va a ir a comprobar de
 * dónde salió.
 */

/** El color de cada familia de hecho, el mismo criterio que el filtro de casos. */
const COLOR_HECHO: Record<string, string> = {
  caso: "#f43f5e",
  conducta: "#f59e0b",
  movimiento: "#38bdf8",
  registro: "#94a3b8",
};

/**
 * El símbolo de cada comportamiento. Sólo para la leyenda: sobre el mapa el
 * comportamiento se lee en el COLOR del tramo de la línea, no en iconos.
 *
 * Estuvieron como iconos sobre el recorrido —un `OverlayView` por tramo— y fue
 * un error por dos motivos. Un día largo pone decenas de overlays en el DOM y
 * cada uno se repinta en cada movimiento del mapa: de ahí el parpadeo al
 * interactuar. Y un símbolo suelto junto a la línea no dice a qué pedazo de
 * línea se refiere, así que tampoco informaba.
 */
const ICONO_TRAMO: Record<string, string> = {
  conduciendo: "▬",
  trafico: "▬",
  ralenti: "▬",
  detenido: "▬",
  sin_datos: "▬",
};

const COLOR_TRAMO: Record<string, string> = {
  conduciendo: "#22c55e",
  trafico: "#f59e0b",
  ralenti: "#38bdf8",
  detenido: "#94a3b8",
  sin_datos: "#f43f5e",
};

export interface RastroDelDiaProps {
  deviceId: string | null;
  /** El caso abierto, para ponerle el radar a su rombo. */
  casoActivoId?: string | null;
  /** Cuántas horas hacia atrás desde ahora. */
  horas: number;
  /** Se llama con el caso del punto tocado; los hechos sin caso no abren nada. */
  onCaso?: (casoId: string) => void;
  /** Dónde estaba ese hecho, para que el mapa se centre en él igual que se
   *  centra en una unidad al seleccionarla. */
  onFoco?: (pos: { lat: number; lng: number }) => void;
}

export default function RastroDelDia({
  deviceId,
  horas,
  casoActivoId = null,
  onCaso,
  onFoco,
}: RastroDelDiaProps) {
  // Una ventana móvil hacia atrás desde AHORA, no un rango de horas del reloj.
  // «Las últimas seis» es la pregunta que se hace quien vigila; «de 08 a 14»
  // es la que se hace quien audita, y ésa vive en el reporte.
  //
  // LAS HORAS VIAJAN AL SERVIDOR. Antes esto pedía el día sin fecha —o sea el
  // día natural— y recortaba en el cliente, que es lo mismo sólo mientras no
  // se cruce la medianoche. Después del corte del día no lo era: a las 00:30
  // «las últimas tres horas» empiezan a las 21:30 de ayer, pero el payload
  // arrancaba a las 00:00 y no había nada que recortar. El rastro salía vacío
  // hacia atrás y parecía que la unidad no existía antes del corte.
  const { data } = useCentinelaUnitConduccion(deviceId, { horas, recorrido: true });
  const dia = data?.conduccion ?? null;

  // El servidor ya contestó la ventana pedida; esto sólo alinea el corte con
  // el reloj del navegador mientras la respuesta envejece en el caché.
  const desde = useMemo(
    () => (data?.ventana?.desde ?? Date.now() - horas * 3600_000),
    [data?.ventana?.desde, horas],
  );

  /*
    ── El recorrido, partido por comportamiento ──────────────────────────────
    Una polilínea por tramo, con su color: verde conduciendo, ámbar a vuelta de
    rueda, azul en ralentí, gris detenida, rojo sin reportar. Así el CÓMO se lee
    sobre la propia línea en vez de en iconos sueltos al lado.

    Los puntos se reparten por tiempo contra los tramos ya calculados por el
    cerebro, y cada segmento arranca en el último punto del anterior para que no
    queden huecos entre colores.
  */
  const segmentos = useMemo(() => {
    const puntos = (dia?.recorrido ?? []).filter((p) => p.ts >= desde);
    const tramos = (dia?.medicion?.tramos ?? []).filter((tr) => tr.hasta >= desde);
    if (puntos.length < 2) return [];

    if (!tramos.length) {
      return [
        {
          key: "todo",
          estado: "conduciendo",
          path: puntos.map((p) => ({ lat: p.lat, lng: p.lon })),
        },
      ];
    }

    return tramos
      .map((tr, i) => {
        const dentro = puntos.filter((p) => p.ts >= tr.desde && p.ts <= tr.hasta);
        // Se engancha con el primero del siguiente tramo: sin eso la línea
        // queda cortada en cada cambio de color.
        const siguiente = puntos.find((p) => p.ts > tr.hasta);
        const path = [...dentro, ...(siguiente ? [siguiente] : [])].map((p) => ({
          lat: p.lat,
          lng: p.lon,
        }));
        return { estado: tr.estado, path, key: `${tr.desde}-${i}` };
      })
      .filter((sg) => sg.path.length > 1);
  }, [dia, desde]);

  const pines = useMemo(
    () => (dia?.hechos ?? []).filter((h) => h.ubicado && h.lat != null && h.ts >= desde),
    [dia, desde],
  );

  if (!dia) return null;

  return (
    <>
      {segmentos.map((sg, i) => (
        <Polyline
          key={sg.key || i}
          path={sg.path}
          options={{
            strokeColor: COLOR_TRAMO[sg.estado] ?? "#22d3ee",
            strokeOpacity: sg.estado === "sin_datos" ? 0.45 : 0.85,
            strokeWeight: 3,
            clickable: false,
            // Debajo de los rombos y de las unidades vivas: el rastro es
            // contexto, no lo que se viene a tocar.
            zIndex: 1,
          }}
        />
      ))}

      {/*
        Los hechos van en ROMBO y no en círculo: el círculo es la unidad viva,
        y con las dos formas iguales el rastro se confundía con la posición
        actual. La forma distingue mejor que el color — el color ya está
        ocupado diciendo de qué familia es el hecho.
      */}
      {pines.map((h, i) => {
        const color = COLOR_HECHO[h.tipo] ?? COLOR_HECHO.registro;
        const abrible = Boolean(h.caso_id);
        const activo = Boolean(casoActivoId && h.caso_id === casoActivoId);

        return (
          <OverlayView
            key={`h-${h.ts}-${i}`}
            position={{ lat: h.lat as number, lng: h.lon as number }}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            /*
              El centrado lo hace la LIBRERÍA, no CSS.

              Estaba con `-translate-x-1/2 -translate-y-1/2`: el overlay se
              coloca en la coordenada y luego el navegador lo corre media caja,
              y esas dos operaciones no ocurren en el mismo fotograma. Al hacer
              zoom se veía el salto. Con `getPixelPositionOffset` la posición
              final se calcula antes de pintar, en una sola pasada.
            */
            getPixelPositionOffset={(ancho, alto) => ({
              x: -ancho / 2,
              y: -alto / 2,
            })}
          >
            {/*
              ⚠️ UN SOLO hijo. `OverlayView` hace `React.Children.only`, así que
              dos hermanos aquí tumban el mapa entero con «expected to receive a
              single React element child».
            */}
            {/* La caja mide EXACTAMENTE lo que el rombo, y el rombo la llena.
                Antes la caja era de 14 px con el rombo de 10 centrado dentro
                por `inset`: el offset de la librería centraba la caja, no el
                rombo, y el sobrante de dos píxeles por lado lo dejaba corrido
                respecto de la línea del recorrido. */}
            <div className="relative h-2.5 w-2.5">
              {activo ? (
                // El MISMO radar que marca a la unidad seleccionada: mismo
                // tamaño, mismas marcas en cruz y mismo giro. Uno distinto
                // obligaría a aprender dos lenguajes para la misma idea.
                <span
                  className="absolute left-1/2 top-1/2 -ml-[17px] -mt-[17px] h-[34px] w-[34px] rounded-full border opacity-90 animate-tacticalSpin motion-reduce:animate-none"
                  style={{ borderColor: color }}
                >
                  <span
                    className="absolute left-1/2 top-[-5px] h-2 w-px -translate-x-1/2"
                    style={{ background: color }}
                  />
                  <span
                    className="absolute left-[-5px] top-1/2 h-px w-2 -translate-y-1/2"
                    style={{ background: color }}
                  />
                </span>
              ) : null}

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  // El foco va SIEMPRE, tenga caso o no: tocar un punto del
                  // recorrido es «llévame ahí», y eso vale igual para una
                  // conducta de cámara que no abre ninguna tarjeta.
                  onFoco?.({ lat: h.lat as number, lng: h.lon as number });
                  if (h.caso_id) onCaso?.(h.caso_id);
                }}
                title={`${h.hora} · ${h.etiqueta}${h.geocerca || h.direccion ? ` · ${h.geocerca || h.direccion}` : ""}`}
                className={`absolute inset-0 rotate-45 transition-transform duration-150 hover:scale-[1.6] ${
                  abrible ? "" : "cursor-default"
                }`}
                style={{
                  background: color,
                  boxShadow: `0 0 0 1.5px rgb(var(--c-surface) / 0.9), 0 0 8px ${color}`,
                }}
              />
            </div>
          </OverlayView>
        );
      })}
    </>
  );
}

/**
 * Lo que el rastro deja fuera y por qué, para el pie del control.
 *
 * Recibe las MISMAS horas que el mapa a propósito: comparte el `queryKey`, así
 * que es la misma petición y no una segunda. Con ventanas distintas serían dos
 * lecturas, y además el pie contaría hechos de un día entero debajo de una
 * línea que sólo dibuja tres horas.
 */
export function useResumenRastro(deviceId: string | null, horas: number) {
  const { data, isLoading } = useCentinelaUnitConduccion(deviceId, { horas, recorrido: true });
  const dia = data?.conduccion ?? null;

  return {
    isLoading,
    puntos: dia?.recorrido_puntos ?? 0,
    hechos: dia?.hechos.length ?? 0,
    sinUbicar: dia?.sin_ubicar ?? 0,
  };
}

export { COLOR_HECHO, ICONO_TRAMO, COLOR_TRAMO };
